import { localeTag } from "./i18n";

// Numbers, money and dates follow the interface language (i18n.ts).
const eur = new Intl.NumberFormat(localeTag, { style: "currency", currency: "EUR" });
const eur0 = new Intl.NumberFormat(localeTag, {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});
const num = new Intl.NumberFormat(localeTag, { maximumFractionDigits: 2 });
const MON = Array.from({ length: 12 }, (_, m) =>
  new Intl.DateTimeFormat(localeTag, { month: "short", timeZone: "UTC" })
    .format(Date.UTC(2026, m, 1))
    .replace(".", ""),
);
const rel = new Intl.RelativeTimeFormat(localeTag, { numeric: "auto", style: "short" });

// While a page shows a record kept in another currency — a loan in USD (ERP-92) — `money` formats
// in that currency. The page sets it as it renders and resets it when it goes (`useMoneyCurrency`).
let shown: { code: string; fmt: Intl.NumberFormat; fmt0: Intl.NumberFormat } | null = null;
export function setMoneyCurrency(code: string | null | undefined) {
  if (!code || code === "EUR") {
    shown = null;
    return;
  }
  if (shown?.code === code) return;
  try {
    shown = {
      code,
      fmt: new Intl.NumberFormat(localeTag, { style: "currency", currency: code }),
      fmt0: new Intl.NumberFormat(localeTag, { style: "currency", currency: code, maximumFractionDigits: 0 }),
    };
  } catch {
    shown = null;
  }
}
export const money = (n?: number | null) => (shown?.fmt ?? eur).format(n ?? 0);
export const money0 = (n?: number | null) => (shown?.fmt0 ?? eur0).format(n ?? 0);
/** Always in the company's currency, whatever the page is showing (ERP-139). */
export const moneyBase = (n?: number | null) => eur.format(n ?? 0);
const inCodes = new Map<string, Intl.NumberFormat>();
/** An amount in a named currency: a list mixing documents in several shows each in its own. */
export function moneyIn(code: string | null | undefined, n?: number | null) {
  if (!code || code === "EUR") return eur.format(n ?? 0);
  let f = inCodes.get(code);
  if (!f) {
    try {
      f = new Intl.NumberFormat(localeTag, { style: "currency", currency: code });
    } catch {
      return `${(n ?? 0).toFixed(2)} ${code}`;
    }
    inCodes.set(code, f);
  }
  return f.format(n ?? 0);
}
export const qty = (n?: number | null) => num.format(n ?? 0);
const pctFmt = new Intl.NumberFormat(localeTag, {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
export const pct = (n?: number | null) => (n == null || !Number.isFinite(n) ? "—" : pctFmt.format(n));
export const compact = (n: number) => {
  const a = Math.abs(n);
  const sign = shown ? `${shown.code} ` : "€";
  if (a >= 1e6) return `${sign}${(n / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M`;
  if (a >= 1e3) return `${sign}${(n / 1e3).toFixed(a >= 1e5 ? 0 : 1)}k`;
  return (shown?.fmt0 ?? eur0).format(n);
};

/** "1 Nov 2026", joined with non-breaking spaces so a date never breaks across lines in a narrow
 *  column. */
export const date = (iso?: string | null) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${Number(d)}\u00a0${MON[Number(m) - 1]}\u00a0${y}`;
};
export const shortDate = (iso?: string | null) => {
  if (!iso) return "—";
  const [, m, d] = iso.slice(0, 10).split("-");
  return `${Number(d)} ${MON[Number(m) - 1]}`;
};
export const dateTime = (iso?: string | null) => {
  if (!iso) return "—";
  const t = new Date(iso);
  return `${shortDate(iso)} ${t.toLocaleTimeString(localeTag, { hour: "2-digit", minute: "2-digit" })}`;
};
export const monthLabel = (ym: string) => MON[Number(ym.slice(5, 7)) - 1];
export const todayIso = () => new Date().toISOString().slice(0, 10);
export const daysBetween = (a: string, b: string) =>
  Math.round((new Date(b).getTime() - new Date(a).getTime()) / 864e5);

export const relTime = (iso?: string | null) => {
  if (!iso) return "—";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 0) return rel.format(Math.ceil(-s / 60), "minute");
  if (s < 60) return rel.format(0, "second");
  if (s < 3600) return rel.format(-Math.floor(s / 60), "minute");
  if (s < 86400) return rel.format(-Math.floor(s / 3600), "hour");
  if (s < 86400 * 7) return rel.format(-Math.floor(s / 86400), "day");
  return shortDate(iso);
};

export const initials = (name?: string | null) =>
  (name || "?")
    .split(/\s+/)
    .filter((w) => /\p{L}/u.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

export const label = (s: string) => (s[0]?.toUpperCase() ?? "") + s.slice(1).replaceAll("_", " ");
